// Spanish (es-MX) formatting helpers. Always pass the business timezone explicitly for dates.
// null / undefined means unknown and renders as an em dash — never as 0.

export const EMPTY = '—';
const LOCALE = 'es-MX';

type DateInput = Date | string | number;

function toDate(v: DateInput): Date {
  return v instanceof Date ? v : new Date(v);
}

function isValid(d: Date): boolean {
  return !Number.isNaN(d.getTime());
}

/** "28 sept 2026, 10:30" in the given IANA timezone. */
export function formatDateTime(v: DateInput | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  const d = toDate(v);
  if (!isValid(d)) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { dateStyle: 'medium', timeStyle: 'short', timeZone, hourCycle: 'h23' }).format(d);
}

/** "28 sept 2026" in the given timezone. */
export function formatDate(v: DateInput | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  const d = toDate(v);
  if (!isValid(d)) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric', timeZone }).format(d);
}

/** "lunes, 28 de septiembre" in the given timezone. */
export function formatLongDate(v: DateInput | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  const d = toDate(v);
  if (!isValid(d)) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', timeZone }).format(d);
}

/** "10:30" (24 h) in the given timezone. */
export function formatTime(v: DateInput | null | undefined, timeZone: string): string {
  if (v === null || v === undefined) return EMPTY;
  const d = toDate(v);
  if (!isValid(d)) return EMPTY;
  return new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', timeZone, hourCycle: 'h23' }).format(d);
}

/**
 * Human timezone label, e.g. "Ciudad de México (GMT-6)". Uses the offset in effect at `at` (default now).
 */
export function formatTimezone(timeZone: string, at: DateInput = new Date()): string {
  const city = timeZone.split('/').pop()?.replace(/_/g, ' ') ?? timeZone;
  const pretty = city === 'Mexico City' ? 'Ciudad de México' : city;
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortOffset' })
      .formatToParts(toDate(at))
      .find((p) => p.type === 'timeZoneName')?.value;
    return part ? `${pretty} (${part})` : pretty;
  } catch {
    return timeZone;
  }
}

/** "hace 5 minutos", "en 2 horas", "ayer". Past and future supported. */
export function formatRelative(v: DateInput | null | undefined, now: DateInput = new Date()): string {
  if (v === null || v === undefined) return EMPTY;
  const d = toDate(v);
  if (!isValid(d)) return EMPTY;
  const diffSec = Math.round((d.getTime() - toDate(now).getTime()) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
  if (abs < 45) return 'justo ahora';
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diffSec / 86400), 'day');
  if (abs < 86400 * 365) return rtf.format(Math.round(diffSec / (86400 * 30)), 'month');
  return rtf.format(Math.round(diffSec / (86400 * 365)), 'year');
}

/** Duration in seconds → "45 s", "4 min 32 s", "1 h 5 min". null → "—". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return EMPTY;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rs = s % 60;
  if (h === 0) return rs === 0 ? `${m} min` : `${m} min ${rs} s`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export interface Money {
  /** Integer amount in the currency's minor unit, as a decimal string (BigInt-safe). */
  amountMinor: string;
  /** ISO 4217 code, e.g. "MXN". */
  currency: string;
}

const MINOR_RE = /^-?\d+$/;

/**
 * Formats money without ever converting minor units to a float:
 * { amountMinor: "350000000", currency: "MXN" } → "$3,500,000.00".
 * The minor-unit exponent comes from Intl (MXN/USD = 2, JPY = 0).
 */
export function formatMoney(value: Money | null | undefined): string {
  if (!value || !MINOR_RE.test(value.amountMinor)) return EMPTY;
  const nf = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: value.currency });
  const digits = nf.resolvedOptions().maximumFractionDigits ?? 2;
  const negative = value.amountMinor.startsWith('-');
  const abs = BigInt(negative ? value.amountMinor.slice(1) : value.amountMinor);
  const scale = 10n ** BigInt(digits);
  const whole = (abs / scale).toString();
  const frac = (abs % scale).toString().padStart(digits, '0');
  // Intl formats decimal strings exactly (no float rounding).
  const decimal = `${negative ? '-' : ''}${whole}${digits > 0 ? `.${frac}` : ''}` as `${number}`;
  return nf.format(decimal);
}

export function formatNumber(v: number | null | undefined, opts: Intl.NumberFormatOptions = {}): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return EMPTY;
  return new Intl.NumberFormat(LOCALE, opts).format(v);
}

/** Ratio 0..1 → "12.5 %". */
export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return EMPTY;
  return new Intl.NumberFormat(LOCALE, { style: 'percent', maximumFractionDigits: digits }).format(ratio);
}

/** Bytes → "2.4 MB". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return EMPTY;
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: i === 0 ? 0 : 1 }).format(v)} ${units[i]}`;
}

/**
 * E.164 → readable phone. Mexico: "+525500000001" → "+52 55 0000 0001" (2-digit area codes 55/33/81),
 * otherwise "+52 222 000 0001". NANP: "+1 555 000 0001". Unknown formats are returned unchanged.
 */
export function formatPhone(e164: string | null | undefined): string {
  if (!e164) return EMPTY;
  const digits = e164.replace(/[^\d]/g, '');
  if (!e164.trim().startsWith('+') || digits.length < 8) return e164;
  if (digits.startsWith('52')) {
    let n = digits.slice(2);
    if (n.length === 11 && n.startsWith('1')) n = n.slice(1); // legacy mobile prefix
    if (n.length === 10) {
      const two = ['55', '33', '81'].includes(n.slice(0, 2));
      return two ? `+52 ${n.slice(0, 2)} ${n.slice(2, 6)} ${n.slice(6)}` : `+52 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
    }
  }
  if (digits.startsWith('1') && digits.length === 11) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  return e164;
}

/** "Mariana López" → "ML". Ignores leading punctuation. */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[^\p{L}]+/u, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
